import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { newAttachmentList } from 'views/IssueDetails/utils/utils';
import { AttachmentTitle } from './Styles';
import AddIcon from '@mui/icons-material/Add';
import logger from 'utils/logger';
import { doesUserHaveSeimitsuViewerPrivilege } from 'utils/privilege';
import localData from 'utils/localData';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API } from 'shared/constants/apis';
import api from 'utils/api';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('AttachmentList');

const AttachmentList = ({
  handleOpenAllViewerButtonClick,
  issue,
  isDoctor,
  handleCommentButtonClick,
  handleDownloadButtonClick,
  handleDicomPreviewButtonClick,
  handlePreviewButtonClick,
  handleViewerButtonClick,
  handleAddAttachmentButtonClick,
  shouldAllowAddSenderAttachment,
  shouldAllowAddReceiverAttachment,
  reloadIssue,
}) => {
  const [dicomFileIncluded, setDicomFileIncluded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attachmentToBeDeleted, setAttachmentToBeDeleted] = useState(null);
  const isSeimitsuViewerPrivilege = doesUserHaveSeimitsuViewerPrivilege();
  const currentOrgId = parseInt(
    localData.get(USER_DATA.CURRENT_ORGANIZATION_ID)
  );

  useEffect(() => {
    //! Setting the viewer button
    const hasDicom = checkIfIssueHasDicomFiles(issue);
    setDicomFileIncluded(hasDicom);
  }, [issue]);

  const handleDeleteIssueAttachmentButtonClick = (attachment) => {
    setAttachmentToBeDeleted(attachment);
    setIsModalOpen(true);
  };

  // The username header is not included in the attachment delete API
  const handleDeleteAttachment = async () => {
    try {
      await api.delete(
        `${DOLPHIN_API.ATTACHMENT}/${attachmentToBeDeleted.id}`,
        {
          params: {
            issueId: issue.id,
            organizationId: currentOrgId,
          },
        }
      );
      enqueueSnackbar(
        intl.get('issue_details_attachment_deleted_successfully', {
          attachmentName: attachmentToBeDeleted.title,
        }),
        {
          variant: 'success',
        }
      );
      setAttachmentToBeDeleted(null);
      setIsModalOpen(false);
      reloadIssue();
    } catch (error) {
      log.error('error', error);
      enqueueSnackbar(intl.get('issue_details_attachment_deletion_failed'), {
        variant: 'error',
      });
    }
  };

  const checkIfIssueHasDicomFiles = () => {
    if (!isSeimitsuViewerPrivilege) {
      return false;
    }

    const hasDicomInSrcOrg = issue.attachmentsOwnedBySrcOrg.some(
      (attachment) => Object.keys(attachment.dicomFilesInfo).length > 0
    );

    if (hasDicomInSrcOrg) {
      return true;
    }

    const hasDicomInDstOrg = issue.attachmentsOwnedByDstOrg.some(
      (attachment) => Object.keys(attachment.dicomFilesInfo).length > 0
    );

    if (hasDicomInDstOrg) {
      return true;
    }

    return false;
  };

  return (
    <>
      {/* Sender attachment files */}
      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{ padding: '5px 0px' }}>
        <Grid style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <AttachmentTitle
            style={{
              fontSize: '16px',
            }}>
            📁 {intl.get('issue_details_text_attachments')}
          </AttachmentTitle>
        </Grid>

        {/* {'ViewerですべてのDicom画像を読影'} */}
        {dicomFileIncluded && (
          <Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ justifyContent: 'start', color: '#fff' }}
              onClick={handleOpenAllViewerButtonClick}
              startIcon={<VisibilityIcon />}>
              {intl.get('issue_details_attachment_list_button_open_viewer')}
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={0}>
        <Grid
          style={{
            padding: '5px 0px',
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
          }}>
          <AttachmentTitle style={{ fontSize: '14px' }}>
            📥 {intl.get('issue_details_sender_attachment_files')}
          </AttachmentTitle>
        </Grid>
        {shouldAllowAddSenderAttachment && (
          <Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                justifyContent: 'start',
                color: '#FFF',
                backgroundColor: '#0B875B',
                '&:hover': {
                  backgroundColor: '#0B875B',
                },
              }}
              onClick={() => handleAddAttachmentButtonClick(true)}>
              {intl.get('issue_details_add_more_attachment_files')}
            </Button>
          </Grid>
        )}
      </Grid>
      {newAttachmentList({
        issue,
        currentOrgId,
        attachments: issue.attachmentsOwnedBySrcOrg,
        dicomFilePaths: issue.dicomFilePaths,
        isDoctor,
        handleDeleteIssueAttachmentButtonClick,
        handleCommentButtonClick,
        handleDownloadButtonClick,
        handleDicomPreviewButtonClick,
        handlePreviewButtonClick,
        handleViewerButtonClick,
        isSeimitsuViewerPrivilege,
      })}

      {/* Receiver attachment files */}
      <Grid container spacing={0}>
        <Grid
          style={{
            padding: '5px 0px',
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
          }}>
          <AttachmentTitle style={{ fontSize: '14px' }}>
            📤 {intl.get('issue_details_receiver_attachment_files')}
          </AttachmentTitle>
        </Grid>
        {shouldAllowAddReceiverAttachment && (
          <Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              sx={{
                justifyContent: 'start',
                color: '#FFF',
                backgroundColor: '#0B875B',
                '&:hover': {
                  backgroundColor: '#0B875B',
                },
              }}
              onClick={() => handleAddAttachmentButtonClick(true)}>
              {intl.get('issue_details_add_more_attachment_files')}
            </Button>
          </Grid>
        )}
      </Grid>
      {newAttachmentList({
        issue,
        currentOrgId,
        attachments: issue.attachmentsOwnedByDstOrg,
        dicomFilePaths: issue.dicomFilePaths,
        isDoctor,
        handleDeleteIssueAttachmentButtonClick,
        handleCommentButtonClick,
        handleDownloadButtonClick,
        handleDicomPreviewButtonClick,
        handlePreviewButtonClick,
        handleViewerButtonClick,
        isSeimitsuViewerPrivilege,
      })}

      <ConfirmModal
        isOpened={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={handleDeleteAttachment}
        title={intl.get('issue_details_confirm_modal_title_delete_attachment')}
        bodyText={intl.get(
          'issue_details_confirm_modal_text_delete_attachment',
          {
            attachmentName: attachmentToBeDeleted?.title,
          }
        )}
        confirmButtonIsLoadingButton={attachmentToBeDeleted !== null}
        confirmButtonText={intl.get(
          'issue_details_confirm_modal_button_delete_attachment'
        )}
        cancelButtonText={intl.get(
          'issue_details_confirm_modal_button_delete_attachment_cancel'
        )}
      />
    </>
  );
};

AttachmentList.propTypes = {
  handleOpenAllViewerButtonClick: PropTypes.func.isRequired,
  issue: PropTypes.object.isRequired,
  isDoctor: PropTypes.bool,
  darkMode: PropTypes.bool,
  handleCommentButtonClick: PropTypes.func,
  handleDownloadButtonClick: PropTypes.func,
  handleDicomPreviewButtonClick: PropTypes.func,
  handlePreviewButtonClick: PropTypes.func,
  handleViewerButtonClick: PropTypes.func,
  handleAddAttachmentButtonClick: PropTypes.func,
  shouldAllowAddSenderAttachment: PropTypes.bool,
  shouldAllowAddReceiverAttachment: PropTypes.bool,
  reloadIssue: PropTypes.func.isRequired,
};

export default AttachmentList;
